'use client';

import { ReactNode } from 'react';
import Link from 'next/link';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import type { SxProps, Theme } from '@mui/material/styles';

const merge = (base: SxProps<Theme>, extra?: SxProps<Theme>): SxProps<Theme> =>
    extra ? [base, ...(Array.isArray(extra) ? extra : [extra])] : base;

export function DocEyebrow({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
    return (
        <Typography
            sx={merge(
                {
                    color: 'var(--accent)',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    mb: 1,
                },
                sx
            )}
        >
            {children}
        </Typography>
    );
}

export function DocTitle({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
    return (
        <Typography
            variant="h1"
            sx={merge(
                {
                    fontSize: { xs: '1.75rem', md: '2.25rem' },
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.15,
                    color: 'var(--fg)',
                    mb: 1.5,
                },
                sx
            )}
        >
            {children}
        </Typography>
    );
}

export function DocSubtitle({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
    return (
        <Typography
            sx={merge(
                {
                    fontSize: { xs: '1rem', md: '1.0625rem' },
                    lineHeight: 1.6,
                    color: 'var(--fg-muted)',
                    maxWidth: '68ch',
                    mb: 4,
                },
                sx
            )}
        >
            {children}
        </Typography>
    );
}

export function DocSection({ children, id }: { children: ReactNode; id?: string }) {
    return (
        <Typography
            variant="h2"
            id={id}
            sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: 'var(--fg)',
                mt: 5,
                mb: 1.5,
                scrollMarginTop: '88px',
            }}
        >
            {children}
        </Typography>
    );
}

export function DocSubsection({ children }: { children: ReactNode }) {
    return (
        <Typography
            variant="h3"
            sx={{
                fontSize: { xs: '1.05rem', md: '1.15rem' },
                fontWeight: 600,
                color: 'var(--fg)',
                mt: 3.5,
                mb: 1,
            }}
        >
            {children}
        </Typography>
    );
}

export function DocText({ children }: { children: ReactNode }) {
    return (
        <Typography sx={{ color: 'var(--fg-muted)', lineHeight: 1.7, mb: 2 }}>
            {children}
        </Typography>
    );
}

export function DocCode({ children, label }: { children: string; label?: string }) {
    return (
        <Box sx={{ mb: 3 }}>
            {label && (
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
                    {label}
                </Typography>
            )}
            <Box
                component="pre"
                sx={{
                    backgroundColor: 'var(--code-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)',
                    p: 2,
                    m: 0,
                    overflow: 'auto',
                    fontFamily: 'var(--font-mono), monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.6,
                    color: 'var(--fg)',
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

export function DocAnswer({ children }: { children: ReactNode }) {
    return (
        <Box
            sx={{
                borderLeft: '3px solid var(--accent)',
                backgroundColor: 'var(--accent-subtle)',
                borderRadius: '0 var(--radius-lg) var(--radius-lg) 0',
                p: 2.5,
                mb: 4,
            }}
        >
            <Typography sx={{ color: 'var(--fg)', lineHeight: 1.7, fontSize: '1rem' }}>
                {children}
            </Typography>
        </Box>
    );
}

export function DocBeforeAfter({ before, after }: { before: string; after: string }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
                    Before
                </Typography>
                <Box component="pre" sx={{
                    backgroundColor: 'var(--code-bg)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)', p: 2, m: 0, overflow: 'auto',
                    fontFamily: 'var(--font-mono), monospace', fontSize: '0.85rem', lineHeight: 1.6, color: 'var(--fg)',
                }}>
                    {before}
                </Box>
            </Box>
            <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
                    After
                </Typography>
                <Box component="pre" sx={{
                    backgroundColor: 'var(--success-muted)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)', p: 2, m: 0, overflow: 'auto',
                    fontFamily: 'var(--font-mono), monospace', fontSize: '0.85rem', lineHeight: 1.6, color: 'var(--fg)',
                }}>
                    {after}
                </Box>
            </Box>
        </Box>
    );
}

export interface DocRelatedLink {
    href: string;
    title: string;
    description: string;
}

export function DocRelated({ links }: { links: DocRelatedLink[] }) {
    return (
        <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid var(--border)' }}>
            <Typography variant="h2" sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--fg)', mb: 2 }}>
                Related
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {links.map((link) => (
                    <Link key={link.href} href={link.href} style={{ textDecoration: 'none' }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'baseline', gap: 1, py: 1, px: 1,
                            borderRadius: 'var(--radius-md)',
                            transition: 'background-color var(--duration-fast) var(--ease-default)',
                            '&:hover': { backgroundColor: 'var(--accent-subtle)' },
                        }}>
                            <Typography sx={{ fontWeight: 600, color: 'var(--accent)', fontSize: 'var(--font-size-base)', whiteSpace: 'nowrap' }}>
                                {link.title}
                            </Typography>
                            <Typography sx={{ color: 'var(--fg-muted)', fontSize: 'var(--font-size-sm)' }}>
                                {link.description}
                            </Typography>
                            <ArrowForwardIcon sx={{ fontSize: '14px', color: 'var(--fg-subtle)', marginLeft: 'auto', flexShrink: 0, alignSelf: 'center' }} />
                        </Box>
                    </Link>
                ))}
            </Box>
        </Box>
    );
}

export function DocMistakeList({ items }: { items: { mistake: string; fix: string }[] }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
            {items.map((item, i) => (
                <Box key={i} sx={{
                    border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
                    p: 2, backgroundColor: 'var(--surface)',
                }}>
                    <Typography sx={{ color: 'var(--fg)', fontWeight: 600, fontSize: 'var(--font-size-base)', mb: 0.5 }}>
                        {item.mistake}
                    </Typography>
                    <Typography sx={{ color: 'var(--fg-muted)', fontSize: 'var(--font-size-base)', lineHeight: 1.6 }}>
                        {item.fix}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}
